import {
  Box,
  IconButton,
  Link,
  TrashIcon,
  Typography,
} from '@customink/pigment-react';
import type {
  Design,
  ProjectItem,
} from '@customink/project-service-react-components';
import {
  useDesigns,
  useMobileBreakpoint,
} from '@customink/project-service-react-components';
import Skeleton from '@mui/material/Skeleton';
import { useCallback, useState } from 'react';

import { googleAnalyticsTag } from '../vendors/googleAnalytics';

import { DeleteConfirmationModal } from '@/components/DeleteConfirmationModal';
import { GA_EVENTS } from '@/constants';
import { useEditDesignLink } from '@/hooks/Design/useEditDesignLink';
import { useInternalPreview } from '@/hooks/internalPreview';
import { useDeleteItems } from '@/hooks/ProjectItem';
import { colorWhite, spacing16 } from '@/utils/designTokens';
import { pageViewTag } from '@/utils/metrics';

export function ProjectItemListHeader({
  designCid,
  projectItems,
}: {
  designCid?: Design['cid'];
  projectItems: Array<Partial<ProjectItem>>;
}) {
  const designs = useDesigns();
  const design = designCid ? designs[designCid] : undefined;
  const isInternalPreview = useInternalPreview();

  const [deleteProjectItems] = useDeleteItems();
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState<boolean>(false);

  const designName = design?.designName?.split('_')[0];
  const title = designName || (
    <Skeleton
      data-testid="title-loading"
      height="20px"
      variant="rounded"
      width="70px"
    />
  );

  const handleEditDesignLink = useEditDesignLink(designCid);

  const handleEditClick = () => {
    pageViewTag('checkout/summary/editdesign');
    googleAnalyticsTag({
      event: 'cart_edit_design',
      interaction_type: 'cart link',
    });
    handleEditDesignLink();
  };

  const handleDelete = useCallback(() => {
    setShowDeleteConfirmationModal(true);
    pageViewTag('checkout/summary/deletedesign');
  }, []);
  const handleCloseDeleteConfirmationModal = useCallback(
    () => setShowDeleteConfirmationModal(false),
    [],
  );

  const projectItemIds = projectItems
    .map((projectItems) => projectItems.id)
    .filter((id) => !!id) as string[];

  const deleteItems = useCallback(() => {
    if (!projectItemIds?.length) return;

    googleAnalyticsTag({
      event: GA_EVENTS.REMOVE_FROM_CART,
    });

    deleteProjectItems(projectItemIds);
  }, [deleteProjectItems, projectItemIds]);

  const isMobile = useMobileBreakpoint();

  return (
    <Box data-testid="project-item-list-header">
      <Box
        sx={{
          backgroundColor: colorWhite,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            gap: spacing16,
            flexGrow: '1',
          }}
        >
          <Typography variant="heading2">{title}</Typography>
          {!isInternalPreview && (
            <Link
              button
              data-testid="edit-design-link"
              fontSize={isMobile ? '14px' : '16px'}
              onClick={handleEditClick}
              textAlign="left"
            >
              Edit Design
            </Link>
          )}
        </Box>
        {!isInternalPreview && (
          <IconButton
            aria-label="Delete"
            color="secondary"
            data-testid="delete-button"
            onClick={handleDelete}
            size="large"
            sx={{
              position: 'absolute',
              top: isMobile ? '4px' : '8px',
              right: isMobile ? '4px' : '8px',
            }}
          >
            <TrashIcon fontSize="medium" />
          </IconButton>
        )}
      </Box>
      {designName && (
        <DeleteConfirmationModal
          designName={designName}
          onClick={deleteItems}
          onClose={handleCloseDeleteConfirmationModal}
          open={showDeleteConfirmationModal}
        />
      )}
    </Box>
  );
}
